import React, { useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50">
      <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-bold text-gray-900 leading-6">{message}</h3>
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            onClick={onConfirm}
          >
            Excluir
          </button>
        </div>
        <button
          onClick={onCancel}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
        >
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

const SIZES = {
  sm: { box: 'h-[136px]', name: 'text-[12px]' },
  md: { box: 'h-[150px]', name: 'text-[13px]' },
};

export default function CardIncorporadoraStyled({
  id,
  usuarioId,
  nome,
  imagem,
  onEdit,
  onDeleted,
  title,
  className = '',
  size = 'md',
}) {
  const dims = SIZES[size] ?? SIZES.md;
  const tooltip = title || nome || 'Incorporadora';
  const { usuario } = useAuth();
  const [deletando, setDeletando] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const podeExcluir = !usuarioId || usuarioId === usuario?.uid;

  const handleExcluir = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!podeExcluir) return;
    setOpenConfirm(true);
  };

  const confirmDeletion = async () => {
    try {
      setDeletando(true);
      await deleteDoc(doc(db, 'incorporadoras', id));
      onDeleted?.(id);
    } finally {
      setDeletando(false);
      setOpenConfirm(false);
    }
  };

  return (
    <div className={`relative group ${className}`}>
      {openConfirm && (
        <ConfirmModal
          message={`Excluir "${nome || 'incorporadora'}"?`}
          onConfirm={confirmDeletion}
          onCancel={() => setOpenConfirm(false)}
        />
      )}

      {/* CARD */}
      <button
        type="button"
        title={tooltip}
        aria-label={tooltip}
        disabled={deletando}
        className={`
          relative w-full ${dims.box}
          rounded-2xl border border-slate-200 bg-white
          shadow-sm transition-all duration-200 ease-out
          hover:shadow-lg hover:-translate-y-1 hover:ring-1 hover:ring-blue-200
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300
          overflow-hidden text-left
          ${deletando ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {/* Área visual – sem fundo branco interno, imagem maior */}
        <div className="px-3 pt-3">
          <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden">
            <img
              src={imagem || '/placeholder-company.png'}
              alt={nome || 'Logo da incorporadora'}
              className="w-full h-full object-contain p-1 md:p-1"
              loading="lazy"
            />

            {/* Recortes sutis + risquinhos azuis */}
            {/* canto inferior esquerdo */}
            <div className="absolute -left-2 -bottom-2 w-10 h-10 bg-white rounded-2xl" />
            <div className="absolute -left-2 -bottom-2 w-10 h-10 rounded-2xl border-l-2 border-b-2 border-blue-400 pointer-events-none" />
            {/* canto superior direito */}
            <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-2xl" />
            <div className="absolute -top-2 -right-2 w-10 h-10 rounded-2xl border-t-2 border-r-2 border-blue-400 pointer-events-none" />
          </div>
        </div>

        {/* Nome */}
        <div className="px-3 pb-2">
          <p
            className={`${dims.name} font-semibold text-slate-900 truncate`}
            title={nome}
          >
            {nome || 'Sem nome'}
          </p>
        </div>
      </button>

      {/* Ações */}
      <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {onEdit && (
          <button
            type="button"
            title="Editar"
            onClick={onEdit}
            className="w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center text-slate-500 hover:bg-white hover:text-blue-500 transition-colors"
          >
            <Pencil size={16} />
          </button>
        )}
        <button
          type="button"
          title={deletando ? 'Excluindo...' : 'Excluir'}
          onClick={handleExcluir}
          disabled={deletando || !podeExcluir}
          className={`w-8 h-8 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center
            ${deletando ? 'text-slate-400 cursor-not-allowed' : 'text-slate-500 hover:bg-white hover:text-red-500 transition-colors'}`}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}
